import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../components/config";
import AutoAssignmentService from "./AutoAssignmentService";

const AssignmentSettingsPanel = () => {
  const [settings, setSettings] = useState({
    auto_assignment_enabled: false,
    auto_assign_chats: true,
    auto_assign_tickets: true,
    default_algorithm: 'round_robin',
    sound_notifications: true,
    browser_notifications: true,
    max_tickets_per_agent: 5,
    max_chats_per_agent: 3,
    auto_escalation_enabled: false,
    escalation_time_minutes: 30
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const fetchedSettings = await AutoAssignmentService.getAssignmentSettings();
      setSettings(fetchedSettings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const success = await AutoAssignmentService.updateAssignmentSettings(settings);
      
      if (success) {
        AutoAssignmentService.showSuccessToast('Settings Saved', 'Assignment settings updated successfully');
      } else {
        AutoAssignmentService.showErrorToast('Save Failed', 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      AutoAssignmentService.showErrorToast('Save Failed', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoAssignment = async () => {
    const newSettings = {
      ...settings,
      auto_assignment_enabled: !settings.auto_assignment_enabled
    };
    
    setSettings(newSettings);
    
    const success = await AutoAssignmentService.updateAssignmentSettings(newSettings);
    
    if (success) {
      if (newSettings.auto_assignment_enabled) {
        AutoAssignmentService.showSuccessToast('Auto-Assignment Enabled', 'New chats and tickets will be automatically assigned');
      } else {
        AutoAssignmentService.showWarningToast('Auto-Assignment Disabled', 'Manual assignment required for all chats and tickets');
      }
    } else {
      // Revert on failure
      setSettings(settings);
      AutoAssignmentService.showErrorToast('Update Failed', 'Failed to update auto-assignment setting');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002966]"></div>
          <span className="ml-2 text-gray-600">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-bold mb-6 text-[#002966]">Assignment Settings</h3>
      
      <div className="space-y-6">
        {/* Main Auto-Assignment Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Auto-Assignment System</span>
            <p className="text-sm text-gray-600 mt-1">
              Automatically assign chats and tickets to available agents
            </p>
          </div>
          <button
            onClick={handleToggleAutoAssignment}
            disabled={saving}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              settings.auto_assignment_enabled
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {settings.auto_assignment_enabled ? '🟢 ON' : '🔴 OFF'}
          </button>
        </div>

        {settings.auto_assignment_enabled && (
          <>
            {/* Assignment Types */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Assignment Types</h4>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="text-gray-700">Auto-Assign New Chats</span>
                <input
                  type="checkbox"
                  checked={settings.auto_assign_chats}
                  onChange={(e) => handleInputChange('auto_assign_chats', e.target.checked)}
                  className="w-4 h-4 text-[#002966] rounded focus:ring-[#ffc000]"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="text-gray-700">Auto-Assign New Tickets</span>
                <input
                  type="checkbox"
                  checked={settings.auto_assign_tickets}
                  onChange={(e) => handleInputChange('auto_assign_tickets', e.target.checked)}
                  className="w-4 h-4 text-[#002966] rounded focus:ring-[#ffc000]"
                />
              </div>
            </div>

            {/* Assignment Algorithm */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Algorithm
              </label>
              <select
                value={settings.default_algorithm}
                onChange={(e) => handleInputChange('default_algorithm', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
              >
                <option value="round_robin">Round Robin</option>
                <option value="fifo">First Available</option>
                <option value="least_busy">Least Busy</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {settings.default_algorithm === 'round_robin' && 'Distribute assignments evenly among all agents'}
                {settings.default_algorithm === 'fifo' && 'Assign to the first available agent'}
                {settings.default_algorithm === 'least_busy' && 'Assign to the agent with fewest current assignments'}
              </p>
            </div>

            {/* Capacity Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Tickets per Agent
                </label>
                <input
                  type="number"
                  value={settings.max_tickets_per_agent}
                  onChange={(e) => handleInputChange('max_tickets_per_agent', parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
                  min="1"
                  max="20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Chats per Agent
                </label>
                <input
                  type="number"
                  value={settings.max_chats_per_agent}
                  onChange={(e) => handleInputChange('max_chats_per_agent', parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
                  min="1"
                  max="20"
                />
              </div>
            </div>

            {/* Notification Settings */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Notifications</h4>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="text-gray-700">Browser Notifications</span>
                <input
                  type="checkbox"
                  checked={settings.browser_notifications}
                  onChange={(e) => handleInputChange('browser_notifications', e.target.checked)}
                  className="w-4 h-4 text-[#002966] rounded focus:ring-[#ffc000]"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="text-gray-700">Sound Notifications</span>
                <input
                  type="checkbox"
                  checked={settings.sound_notifications}
                  onChange={(e) => handleInputChange('sound_notifications', e.target.checked)}
                  className="w-4 h-4 text-[#002966] rounded focus:ring-[#ffc000]"
                />
              </div>
            </div>

            {/* Escalation Settings */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Auto-Escalation</h4>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <span className="text-gray-700">Enable Auto-Escalation</span>
                  <p className="text-xs text-gray-500 mt-1">Escalate unresolved tickets after set time</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.auto_escalation_enabled}
                  onChange={(e) => handleInputChange('auto_escalation_enabled', e.target.checked)}
                  className="w-4 h-4 text-[#002966] rounded focus:ring-[#ffc000]"
                />
              </div>
              
              {settings.auto_escalation_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Escalation Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.escalation_time_minutes}
                    onChange={(e) => handleInputChange('escalation_time_minutes', parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
                    min="5"
                    max="480"
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="pt-4 border-t">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
              saving
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-[#002966] text-white hover:bg-[#001f4d]'
            }`}
          >
            {saving ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentSettingsPanel;
